import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import {
  $createTextNode,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $insertNodes,
  $isParagraphNode,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  KEY_ENTER_COMMAND,
  TextNode
} from "lexical"
import { useEffect, useRef } from "react"

import {
  menuDataId,
  menuItemDataIdPrefix
} from "@/organisms/Ticketing/components/DynamicTagsField/const"
import { $createClosedTagNode, ClosedTagNode } from "../nodes/ClosedTagNode"
import {
  $createDynamicTagNode,
  $isDynamicTagNode,
  DynamicTagNode
} from "../nodes/DynamicTagNode"

import type { DynamicTag } from "@/api/useWorkflow.types"
import type { DynamicTagPayload } from "../DynamicTagsField.types"

const TRIGGER_SINGLE = "{"
const TRIGGER_OPEN = `${TRIGGER_SINGLE}${TRIGGER_SINGLE}`
const TRIGGER_CLOSE = "}}"

interface DynamicTagPluginProps {
  children: (props: {
    replaceWithClosedTag: (tag: DynamicTag) => void
  }) => React.ReactNode
  activeNodeKey: string | null
  keysTags?: { [x in string]: boolean }
  replaceOnEnter?: boolean
  onTagCreate?: (payload: DynamicTagPayload) => void
  onTagInput?: (payload: DynamicTagPayload) => void
  onTagDestroyed?: () => void
}

const createPayload = (
  editor: ReturnType<typeof useLexicalComposerContext>[0],
  nodeKey: string,
  text: string
): DynamicTagPayload | null => {
  const element = editor.getElementByKey(nodeKey)

  if (!element) return null

  const rect = element.getBoundingClientRect()

  let aggregatedText = text

  if (text.startsWith(TRIGGER_OPEN)) aggregatedText = text.slice(2)

  if (aggregatedText.endsWith(TRIGGER_CLOSE))
    aggregatedText = aggregatedText.slice(0, -2)

  return {
    nodeKey,
    triggerText: TRIGGER_OPEN,
    aggregatedText,
    rect
  }
}

export const getKeyFromClosedTagText = (text: string) =>
  (text || "").replace(TRIGGER_OPEN, "").replace(TRIGGER_CLOSE, "")

export const DynamicTagPlugin = (props: DynamicTagPluginProps) => {
  const {
    onTagCreate,
    onTagInput,
    onTagDestroyed,
    children,
    activeNodeKey,
    keysTags,
    replaceOnEnter
  } = props

  const [editor] = useLexicalComposerContext()
  const previousTextRef = useRef<Map<string, string>>(new Map())
  const activeDynamicNodeRef = useRef<string | null>(null)
  const isClosingTagRef = useRef(false)

  const callbacks = useRef({ onTagCreate, onTagInput, onTagDestroyed })

  useEffect(() => {
    callbacks.current = { onTagCreate, onTagInput, onTagDestroyed }
  }, [onTagCreate, onTagInput, onTagDestroyed])

  const replaceWithClosedTag = (
    tag: DynamicTag,
    props: { root?: boolean } = {}
  ) => {
    if (!activeNodeKey && !props.root) return

    editor.update(() => {
      const tagText = `${TRIGGER_OPEN}${tag.key}${TRIGGER_CLOSE}`
      const closedTagNode = $createClosedTagNode(tagText)

      if (props.root) {
        const root = $getRoot()
        const lastChild = root.getLastChild()

        if ($isParagraphNode(lastChild)) lastChild.append(closedTagNode)
        else $insertNodes([closedTagNode])
      } else {
        if (!activeNodeKey) return

        const node = $getNodeByKey(activeNodeKey)

        if (!$isDynamicTagNode(node)) return

        closedTagNode.setFormat(node.getFormat())
        node.replace(closedTagNode)
      }

      closedTagNode.selectEnd()
    })

    onTagDestroyed?.()
  }

  useEffect(() => {
    const removeEnterCommand = editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        if (replaceOnEnter) {
          event?.preventDefault()
          event?.stopPropagation()
          ;(
            document.querySelector(
              `[data-id='${menuItemDataIdPrefix}0']`
            ) as HTMLElement
          )?.click()

          return true
        }

        return false
      },
      COMMAND_PRIORITY_HIGH
    )

    return () => {
      removeEnterCommand()
    }
  }, [editor, replaceOnEnter])

  useEffect(() => {
    const textTracker = previousTextRef.current

    const removeTextTransform = editor.registerNodeTransform(
      TextNode,
      (node: TextNode) => {
        const textNode = node.getTextContent()
        const index = textNode.indexOf(TRIGGER_SINGLE)

        if (index === -1) return

        if (isClosingTagRef.current) return

        const selection = $getSelection()

        if (!$isRangeSelection(selection)) return

        const offset = selection.anchor.offset
        const charBefore = textNode[offset - 2]
        const char = textNode[offset - 1]
        const startWithOpen = textNode.startsWith(TRIGGER_OPEN)

        if (charBefore !== TRIGGER_SINGLE || char !== TRIGGER_SINGLE) {
          const matches = textNode.match(/\{\{.+?\}\}/)

          if (matches && !!keysTags?.[matches[0].slice(2, -2)])
            node.replace($createClosedTagNode(textNode))

          return
        }

        if (!startWithOpen && /^\s*$/.test(textNode[offset - 3]) === false)
          return

        const format = node.getFormat()

        const before = textNode.slice(0, offset - 2)
        const after = textNode.slice(offset)

        const beforeNode = $createTextNode(before)

        beforeNode.setFormat(format)
        node.replace(beforeNode)

        const tagNode = $createDynamicTagNode(TRIGGER_OPEN)

        tagNode.setFormat(format)
        beforeNode.insertAfter(tagNode)

        if (after) {
          const afterNode = $createTextNode(after)

          afterNode.setFormat(format)
          tagNode.insertAfter(afterNode)
        }

        tagNode.selectEnd()

        setTimeout(() => {
          editor.getEditorState().read(() => {
            const payload = createPayload(editor, tagNode.getKey(), "{{")

            if (payload) callbacks.current?.onTagCreate?.(payload)
          })
        }, 0)
      }
    )

    const removeDynamicTagTransform = editor.registerNodeTransform(
      DynamicTagNode,
      (node: DynamicTagNode) => {
        const textNode = node.getTextContent()
        const format = node.getFormat()

        const noStartingTrigger = !textNode.startsWith(TRIGGER_OPEN)
        const endingTrigger = textNode.endsWith(TRIGGER_CLOSE)

        if (noStartingTrigger) {
          const nextNode = $createTextNode(textNode)

          nextNode.setFormat(format)
          node.replace(nextNode)
        } else if (endingTrigger) {
          const closedNode = $createClosedTagNode(textNode)

          closedNode.setFormat(format)
          node.replace(closedNode)
          closedNode.selectEnd()
        } else {
          const payload = createPayload(editor, node.getKey(), textNode)

          if (payload) callbacks.current?.onTagInput?.(payload)
        }
      }
    )

    const removeClosedTagTransform = editor.registerNodeTransform(
      ClosedTagNode,
      (node: ClosedTagNode) => {
        const textNode = node.getTextContent()
        const format = node.getFormat()

        if (
          textNode.startsWith(TRIGGER_OPEN) &&
          textNode.endsWith(TRIGGER_CLOSE) &&
          keysTags?.[getKeyFromClosedTagText(textNode)]
        )
          return

        const partsWithBraces = textNode
          .split(/(\{\{[^{}]*\}\})/)
          .filter(Boolean)

        if (partsWithBraces) {
          const lastIndex = partsWithBraces.length - 1

          partsWithBraces.forEach((part, index) => {
            let nextNode
            const isLast = index === lastIndex
            const keyExists = keysTags?.[getKeyFromClosedTagText(part)]

            if (
              part.startsWith(TRIGGER_OPEN) &&
              part.endsWith(TRIGGER_CLOSE) &&
              keyExists
            )
              nextNode = $createClosedTagNode(part)
            else nextNode = $createTextNode(part)

            nextNode.setFormat(format)

            if (isLast) node.replace(nextNode)
            else node.insertBefore(nextNode)
          })

          return
        }

        const nextNode = $createTextNode(textNode)

        nextNode.setFormat(format)
        node.replace(nextNode)
      }
    )

    const removeUpdateListener = editor.registerUpdateListener(
      ({ editorState }) => {
        editorState.read(() => {
          const selection = $getSelection()

          if (!$isRangeSelection(selection)) return

          const anchorNode = selection.anchor.getNode()

          const isDynamicTag = $isDynamicTagNode(anchorNode)
          const currentKey = isDynamicTag ? anchorNode.getKey() : null
          const prevKey = activeDynamicNodeRef.current

          if (prevKey && prevKey !== currentKey) {
            editor.update(
              () => {
                const node = $getNodeByKey(prevKey)

                if ($isDynamicTagNode(node)) {
                  const text = node.getTextContent()
                  const textNode = $createTextNode(text)

                  node.replace(textNode)
                }
              },
              { discrete: true }
            )

            callbacks.current?.onTagDestroyed?.()
          }

          if (isDynamicTag) activeDynamicNodeRef.current = currentKey
          else activeDynamicNodeRef.current = null
        })
      }
    )

    const removeDynamicTagNodeMutationListener =
      editor.registerMutationListener(
        DynamicTagNode,
        (mutations: Map<string, "created" | "updated" | "destroyed">) => {
          for (const [, mutation] of mutations) {
            if (mutation === "destroyed") {
              callbacks.current?.onTagDestroyed?.()
              break
            }
          }
        }
      )

    const rootElement = editor.getRootElement()

    const handleFocusOut = (e: FocusEvent) => {
      const relatedTarget = e.relatedTarget as Node | null

      const isMenuTarget = (relatedTarget as Element)?.closest?.(
        `[data-id="${menuDataId}"]`
      )

      if (isMenuTarget) return

      const prevKey = activeDynamicNodeRef.current

      if (prevKey) {
        isClosingTagRef.current = true

        editor.update(
          () => {
            const node = $getNodeByKey(prevKey)

            if ($isDynamicTagNode(node)) {
              const text = node.getTextContent()
              const textNode = $createTextNode(text)

              node.replace(textNode)
            }
          },
          { discrete: true }
        )

        setTimeout(() => {
          isClosingTagRef.current = false
        }, 0)

        activeDynamicNodeRef.current = null
        callbacks.current?.onTagDestroyed?.()
      }
    }

    const focusEvent = "focusout"

    rootElement?.addEventListener(focusEvent, handleFocusOut)

    return () => {
      removeTextTransform()
      removeDynamicTagTransform()
      removeClosedTagTransform()
      removeUpdateListener()
      removeDynamicTagNodeMutationListener()
      rootElement?.removeEventListener(focusEvent, handleFocusOut)
      textTracker.clear()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor])

  return children({ replaceWithClosedTag })
}
